import {
  AudioLinesIcon,
  BrainCogIcon,
  ClockIcon,
  DownloadIcon,
  EyeIcon,
  FileIcon,
  ImageIcon,
  LinkIcon,
  MoreVerticalIcon,
  PenIcon,
  SplitIcon,
  TrashIcon,
  VideoIcon,
} from "lucide-react";
import { useState } from "react";
import { AxMediaPreviewContentDialog } from "./AxMediaPreviewContentDialog";
import { AxMediaPreview } from "./AxMediaPreview";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";
import { ContentSelectConfig } from "@/pages/content/ContentCrud";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getContentDetails, getContentFolder } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { hexToAlpha } from "@/lib/colorUtils";
import { UpdateContentStartEndDate } from "@/pages/content/UpdateContentStartEndDate";
import { ContentTags } from "@/pages/content/ContentTags";
import { FileMetadata } from "@/components/FileMetadata";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { formatDateToLocal } from "@/lib/dateUtils";
import { ContentRenameSheet } from "@/pages/content/ContentRenameSheet";
import { ContentLinkUpdateSheet } from "@/pages/content/ContentLinkUpdateSheet";
import { parsePreviewConfig } from "./ParsePreviewConfig";
import { UpdateContentVariablesDialog } from "@/pages/content/UpdateContentVariablesDialog";
import { getIsEndDatePassed } from "@/lib/contentUtils";

interface AxMediaPreviewContentProps {
  contentGuid: string;
  onClickDelete?: () => void;
  isDeleting?: boolean;
  showActions?: boolean;
  selectConfig?: ContentSelectConfig;
}

export const AxMediaPreviewContent: React.FC<AxMediaPreviewContentProps> = ({
  contentGuid,
  onClickDelete,
  isDeleting,
  showActions,
  selectConfig,
}) => {
  const { t } = useTranslation();
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showLinkUpdateDialog, setShowLinkUpdateDialog] = useState(false);
  const [showUpdateVariablesDialog, setShowUpdateVariablesDialog] = useState(false);
  const activeFirm = useSelector(selectActiveFirm);
  const queryClient = useQueryClient();

  const { data: folders } = useQuery({
    queryKey: ["contentFolder"],
    queryFn: () => getContentFolder(String(activeFirm?.firmGuid)),
  });

  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const { goToSplitContentDetail } = useAppNavigate();

  const {
    data: contentDetailsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["contentDetails", contentGuid],
    queryFn: () => getContentDetails(String(activeFirm?.firmGuid), contentGuid),
  });

  const contentDetails = contentDetailsData?.data;

  if (error) {
    return (
      <Alert className="mt-2" variant="destructive">
        <AlertTitle>{t("common.error")}</AlertTitle>
        <AlertDescription>{error?.message}</AlertDescription>
      </Alert>
    );
  }

  if (!contentDetails || isLoading) {
    return null;
  }

  const {
    contentLink,
    contentStorageName,
    contentType,
    name,
    endDate,
    startDate,
    intId: contentIntId,
    folderGuid,
    screenSizeName,
    duration,
    contentSize,
    templateLayoutConfig,
  } = contentDetails;

  const folder = folders?.data?.result.find((folder) => folder.id === folderGuid);
  const folderColor = folder?.color;
  const folderName = folder?.name;

  const parsedPreviewConfig = parsePreviewConfig(contentType, contentStorageName, contentLink, name);
  const isEndDatePassed = endDate ? getIsEndDatePassed(endDate) : true;

  const getPreview = () => {
    if (isDeleting) {
      return (
        <div className="w-full h-[150px] flex items-center justify-center">
          <LoadingSmall />
        </div>
      );
    }
    if (parsedPreviewConfig) {
      return (
        <div onClick={() => setShowPreviewDialog(true)} className="w-full h-[150px] cursor-pointer">
          <AxMediaPreview parsedPreviewConfig={parsedPreviewConfig} />
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      className="relative group hover:shadow-md transition-all duration-200 h-full flex flex-col overflow-hidden"
      style={{
        borderColor: `${folderColor || "hsl(var(--border))"}`,
        borderWidth: "1px",
      }}
    >
      <CardContent className="p-0 relative">
        {/* Preview content */}
        {getPreview()}

        {/* Quick action buttons - visible on hover */}
        {showActions && !selectConfig && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2 z-20">
            {parsedPreviewConfig?.type !== "link" && parsedPreviewConfig?.type !== "split" && (
              <Button
                variant="secondary"
                size="icon"
                className="shadow-md"
                onClick={() => window.open(parsedPreviewConfig?.url, "_blank")}
              >
                <DownloadIcon className="w-4 h-4" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button variant="secondary" size="icon" className="shadow-md">
                  <MoreVerticalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48">
                <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setShowPreviewDialog(true)}>
                  <EyeIcon className="w-4 h-4 mr-2" /> {t("content.preview")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowRenameDialog(true)}>
                  <PenIcon className="w-4 h-4 mr-2" /> {t("common.rename")}
                </DropdownMenuItem>
                {parsedPreviewConfig?.type === "split" && (
                  <DropdownMenuItem onClick={() => goToSplitContentDetail(contentGuid)}>
                    <SplitIcon className="w-4 h-4 mr-2" /> {t("common.update")}
                  </DropdownMenuItem>
                )}
                {parsedPreviewConfig?.type === "link" && (
                  <DropdownMenuItem onClick={() => setShowLinkUpdateDialog(true)}>
                    <LinkIcon className="w-4 h-4 mr-2" /> {t("common.updateLink")}
                  </DropdownMenuItem>
                )}
                {parsedPreviewConfig?.type.startsWith("image") && templateLayoutConfig && (
                  <DropdownMenuItem onClick={() => setShowUpdateVariablesDialog(true)}>
                    <BrainCogIcon className="w-4 h-4 mr-2" /> {t("content.updateImage")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setShowScheduleDialog(true)}>
                  <ClockIcon className="w-4 h-4 mr-2" /> {t("common.validity")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onClickDelete} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                  <TrashIcon className="w-4 h-4 mr-2" /> {t("common.deleteEntity.buttonText")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </CardContent>

      {showActions && (
        <CardFooter className="flex flex-col p-0 mt-auto">
          {" "}
          {/* Added mt-auto */}
          <div className="w-full border-t" style={{ backgroundColor: hexToAlpha(folderColor || "#444444", 0.05) }}>
            {/* Title and folder section */}
            <div className="p-3 border-b border-border/50">
              <div className="font-medium text-sm mb-1 line-clamp-2 flex items-center gap-1">
                {parsedPreviewConfig?.type === "image" && <ImageIcon className="w-4 h-4 mr-2" />}
                {parsedPreviewConfig?.type === "video" && <VideoIcon className="w-4 h-4 mr-2" />}
                {parsedPreviewConfig?.type === "audio" && <AudioLinesIcon className="w-4 h-4 mr-2" />}
                {parsedPreviewConfig?.type === "link" && <LinkIcon className="w-4 h-4 mr-2" />}
                {parsedPreviewConfig?.type === "split" && <SplitIcon className="w-4 h-4 mr-2" />}
                {parsedPreviewConfig?.type === "doc" && <FileIcon className="w-4 h-4 mr-2" />}
                {name}
              </div>
              {folderName && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: folderColor || "#444444" }} />
                  {folderName}
                </div>
              )}
            </div>

            {/* Metadata section */}
            <div className="p-3 space-y-2">
              {(startDate || endDate) && (
                <div className="text-xs space-y-1">
                  {startDate && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <ClockIcon className="w-3 h-3" />
                      <span>
                        {t("common.from")}: {formatDateToLocal(startDate)}
                      </span>
                    </div>
                  )}
                  {endDate && (
                    <div
                      className={`flex items-center gap-1 ${
                        isEndDatePassed ? "text-red-500" : "text-muted-foreground"
                      }`}
                    >
                      <ClockIcon className={`w-3 h-3 ${isEndDatePassed ? "text-red-500" : ""}`} />
                      <span>
                        {t("common.until")}: {formatDateToLocal(endDate)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <FileMetadata
                url={parsedPreviewConfig.url}
                size={contentSize}
                className="text-xs text-muted-foreground"
              />

              <ContentTags contentGuid={contentGuid} contentIntId={contentIntId} />

              {screenSizeName && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">{t("entities.screenSize")}:</span> {screenSizeName}
                </div>
              )}
            </div>
          </div>
          {/* Select button */}
          {selectConfig && selectConfig.types.includes(parsedPreviewConfig.type) && (
            <div className="p-3 w-full border-t">
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() =>
                  selectConfig.onSelect({
                    contentGuid,
                    contentStorageName,
                    contentType,
                    contentIntId,
                    contentDuration: duration,
                  })
                }
              >
                {t("common.select")}
              </Button>
            </div>
          )}
        </CardFooter>
      )}

      {showPreviewDialog && parsedPreviewConfig && (
        <AxMediaPreviewContentDialog
          parsedPreviewConfig={parsedPreviewConfig}
          onClose={() => setShowPreviewDialog(false)}
        />
      )}
      {showScheduleDialog && (
        <UpdateContentStartEndDate
          contentGuid={contentGuid}
          startDate={startDate}
          endDate={endDate}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["content"] });
            queryClient.invalidateQueries({ queryKey: ["contentDetails", contentGuid] });
            setShowScheduleDialog(false);
          }}
          onOpenChange={setShowScheduleDialog}
        />
      )}
      {showRenameDialog && (
        <ContentRenameSheet
          contentGuid={contentGuid}
          name={name}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["content"] });
            queryClient.invalidateQueries({ queryKey: ["contentDetails", contentGuid] });
            setShowRenameDialog(false);
          }}
          onOpenChange={setShowRenameDialog}
        />
      )}
      {showLinkUpdateDialog && (
        <ContentLinkUpdateSheet
          contentGuid={contentGuid}
          link={contentLink}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["content"] });
            queryClient.invalidateQueries({ queryKey: ["contentDetails", contentGuid] });
            setShowLinkUpdateDialog(false);
          }}
          onOpenChange={setShowLinkUpdateDialog}
        />
      )}
      {showUpdateVariablesDialog && (
        <UpdateContentVariablesDialog
          template={JSON.parse(contentDetails.templateLayoutConfig || "{}")}
          content={contentDetails}
          onClose={() => setShowUpdateVariablesDialog(false)}
          onUploadSuccess={function (): void {
            queryClient.invalidateQueries({ queryKey: ["content"] });
            queryClient.invalidateQueries({ queryKey: ["contentDetails", contentGuid] });
            setShowUpdateVariablesDialog(false);
          }}
        />
      )}
    </Card>
  );
};
